import * as i18n from 'i18next';
import { reactI18nextModule } from 'react-i18next';
import {
  DEFAULT_LANGUAGE_CODE,
  HOST,
  LANGUAGE_CODE_EN_CA,
  LANGUAGE_CODE_FR_CA,
  ROUTE_PATH_ACTIVATE,
  ROUTE_PATH_ACTIVATE_STEP_1,
  ROUTE_PATH_ACTIVATE_STEP_2,
  ROUTE_PATH_ACTIVATE_SUCCESS,
  ROUTE_PATH_ALERTS,
  ROUTE_PATH_CONTACT,
  ROUTE_PATH_FAQ,
  ROUTE_PATH_HOME,
  ROUTE_PATH_SCAM,
  ROUTE_PATH_IDENTITY,
  ROUTE_PATH_PRIVACY_POLICY,
  ROUTE_PATH_PROTECTION,
  ROUTE_PATH_RESOURCE_CENTRE,
  ROUTE_PATH_TERMS_CONDITIONS,
  ROUTE_PATH_TERMS_OF_USE
} from '../constants';
import { translations as enCaTranslations } from './en-CA';
import { translations as frCaTranslations } from './fr-CA';

function determineLanguageFromHost(host: string, defaultLanguage: string): string {
  const match = HOST.find((item) => item.key.toLowerCase() === host.toLowerCase());

  // == FOR DEBUGGING PURPOSES ONLY ==
  //if (HOST) {
  //  return LANGUAGE_CODE_FR_CA;
  //}
  // == FOR DEBUGGING PURPOSES ONLY ==

  return match ? match.value : defaultLanguage;
}

const defaultNS = 'global';

const i18nInstance = i18n.use(reactI18nextModule).init({
  lng: determineLanguageFromHost(window.location.host, DEFAULT_LANGUAGE_CODE),
  fallbackLng: LANGUAGE_CODE_EN_CA,

  ns: [
    'global',
    // 'homePage',
    // 'homePageV2',
    'homePageV3',
    'productVideo',
    'alertsPage',
    'identityPage',
    'protectionPage',
    'activatePage',
    'termsAndConditionsPage',
    'termsOfUsePage',
    'privacyPolicyPage',
    'faqPage',
    'contactUsPage',
    'resourceCentrePage',
    'ScamDetectionPage'
  ],
  defaultNS,

  debug: process.env.REACT_APP_ENV !== 'production',
  returnObjects: true,

  interpolation: {
    escapeValue: false // not needed for react!!
  },

  react: {
    wait: true // wait to render() until translations are loaded
  },

  resources: {
    [LANGUAGE_CODE_EN_CA]: enCaTranslations,
    [LANGUAGE_CODE_FR_CA]: frCaTranslations
  }
});

// ****************************************************************************
// route path to translation key mapping (unordered)
//
const DOC_TITLE_MAP: { [key: string]: string } = {
  // [ROUTE_PATH_HOME]: 'homePage:docTitle',
  // [ROUTE_PATH_HOME]: 'homePageV2:docTitle',
  [ROUTE_PATH_HOME]: 'homePageV3:docTitle',
  [ROUTE_PATH_SCAM]: 'ScamDetectionPage:docTitle',
  [ROUTE_PATH_PROTECTION]: 'protectionPage:docTitle',
  [ROUTE_PATH_ALERTS]: 'alertsPage:docTitle',
  [ROUTE_PATH_IDENTITY]: 'identityPage:docTitle',
  [ROUTE_PATH_ACTIVATE]: 'activatePage:docTitle',
  [`${ROUTE_PATH_ACTIVATE}${ROUTE_PATH_ACTIVATE_STEP_1}`]: 'activatePage:steps.personalInfo.title',
  [`${ROUTE_PATH_ACTIVATE}${ROUTE_PATH_ACTIVATE_STEP_2}`]: 'activatePage:steps.contactInfo.title',
  [`${ROUTE_PATH_ACTIVATE}${ROUTE_PATH_ACTIVATE_SUCCESS}`]: 'activatePage:steps.confirmation.progressBadgeTitle',
  [ROUTE_PATH_TERMS_CONDITIONS]: 'termsAndConditionsPage:docTitle',
  [ROUTE_PATH_TERMS_OF_USE]: 'termsOfUsePage:docTitle',
  [ROUTE_PATH_PRIVACY_POLICY]: 'privacyPolicyPage:docTitle',
  [ROUTE_PATH_FAQ]: 'faqPage:docTitle',
  [ROUTE_PATH_CONTACT]: 'contactUsPage:docTitle',
  [ROUTE_PATH_RESOURCE_CENTRE]: 'resourceCentrePage:docTitle'
};

export function getDocumentTitle(pathname: string, options?: i18n.TranslationOptions<object>): string {
  const optsWithNoDefaultValue = Object.assign({}, options, { defaultValue: undefined });

  let docTitlePrefix = '';
  let docTitleKey = '.'; // can't do empty string; i18next considers it a valid key (i18next DEFECT??? FEATURE???)

  const pathnameWithSeparator = pathname.endsWith('/') ? pathname : `${pathname}/`;
  if (pathnameWithSeparator.startsWith(ROUTE_PATH_ACTIVATE)) {
    docTitlePrefix = `${i18n.t(DOC_TITLE_MAP[ROUTE_PATH_ACTIVATE], optsWithNoDefaultValue)} - `;
  }

  if (Object.prototype.hasOwnProperty.call(DOC_TITLE_MAP, pathnameWithSeparator)) {
    docTitleKey = DOC_TITLE_MAP[pathnameWithSeparator];
  }

  const opts = Object.assign({}, { defaultValue: i18n.t('pageNotFound:docTitle', optsWithNoDefaultValue) }, options);
  return `${docTitlePrefix}${i18n.t(docTitleKey, opts)}`;
}

export default i18nInstance;
